import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from '../../components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Bell, Loader2 } from 'lucide-react';
import { InternalOptInRow } from './InternalOptInRow';
import { SubscriptionDomainRow } from './SubscriptionDomainRow';
import { useSubscriptionTree } from './useSubscriptionTree';
import type { NotificationSubscriptionSide } from '../../lib/api';

/** What each side is called on the tab and in the line beneath it. */
const SIDE_LABELS: Record<NotificationSubscriptionSide, string> = {
  customer: 'Customer',
  supplier: 'Supplier',
  all: 'Both sides',
};

/**
 * Per-user notification settings.
 *
 * Shows the subscription tree at domain level — six rows or fewer, filtered
 * server-side to the sections that can actually reach this person's portal
 * side. Each row is an In-app and an Email switch for the whole section, and
 * nothing else.
 *
 * Deliberately not a mirror of Admin Setup. Which events exist, on which
 * channels, at what cadence, is the site's decision; this screen only lets one
 * person take less of it. A choice here can narrow what the site sends, never
 * widen it, so anything the tenant has switched off renders as off and says so
 * instead of offering a switch that would not work.
 *
 * Someone at a company that both buys and sells holds a separate set of these
 * per side, so the tabs appear for them and for nobody else. Without them the
 * second set would be invisible — reachable only by switching portal mode,
 * which is not something a person looking for their notification settings
 * would think to do.
 */
export function NotificationsTab() {
  const {
    tree, loading, saving, side, setSide, setMuted, setInternalOptIn,
  } = useSubscriptionTree();

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin" />
          <span className="ml-2">Loading notification settings...</span>
        </CardContent>
      </Card>
    );
  }

  if (!tree) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          Your notification settings could not be loaded. Refresh to try again.
        </CardContent>
      </Card>
    );
  }

  const emailsEnabled = tree.tenant_defaults.emails_enabled;
  // One side means no tabs: a customer-only or supplier-only user has exactly
  // one set of preferences and should not be shown a chooser with one option.
  const showSides = tree.available_sides.length > 1 && side !== '';
  const sideLabel = side ? SIDE_LABELS[side] : '';
  const otherLabel = SIDE_LABELS[
    tree.available_sides.find(option => option !== side) ?? 'all'
  ];

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Notifications
          </CardTitle>
          <CardDescription>
            Choose how much reaches you. These settings only ever reduce what
            your site sends — they cannot switch on something it has turned off.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {showSides && (
            <>
              <Tabs
                value={side}
                onValueChange={value =>
                  setSide(value as NotificationSubscriptionSide)}
              >
                <TabsList>
                  {tree.available_sides.map(option => (
                    <TabsTrigger key={option} value={option} disabled={saving}>
                      {SIDE_LABELS[option]}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>
              <p className="text-sm text-muted-foreground">
                {`Editing your ${sideLabel.toLowerCase()}-side preferences. `}
                {`Your ${otherLabel.toLowerCase()}-side settings are separate `}
                {'and are not changed here.'}
              </p>
            </>
          )}

          {!emailsEnabled && (
            <p className="rounded-md border bg-muted/50 p-3 text-sm text-muted-foreground">
              Notification email is switched off for your site, so the email
              column is inactive. In-app notifications are unaffected.
            </p>
          )}

          {tree.domains.length === 0 && (
            <p className="py-6 text-center text-sm text-muted-foreground">
              No notifications are configured for your site yet.
            </p>
          )}

          {tree.domains.map(domain => (
            <div key={domain.scope_key} className="space-y-2">
              <SubscriptionDomainRow
                domain={domain}
                emailsEnabled={emailsEnabled}
                saving={saving}
                onMute={setMuted}
              />
              {/* CR-7255 review: customers in the person's own group are off
                  until they opt in; say so instead of showing them as on. */}
              {tree.internal_opt_in && (
                <InternalOptInRow
                  domainKey={domain.domain}
                  domainLabel={domain.label}
                  optIn={tree.internal_opt_in}
                  emailsEnabled={emailsEnabled}
                  saving={saving}
                  onChange={setInternalOptIn}
                />
              )}
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
